import React, { useState } from "react";

import { SearchableDropdown } from "@fronterahealth/frontera-ui-components";

import { MediaCommentFilterEnum } from "@api/graphql/types-and-hooks";
import { CommentThread } from "@pages/VideoSessions/components/Comments/CommentThread";
import { NoData } from "@pages/VideoSessions/components/NoData/NoData";
import { SessionNoteCard } from "@pages/VideoSessions/components/SessionNoteCard/SessionNoteCard";
import { DataLoading } from "@pages/VideoSessions/components/Utils/DataLoading";
import { useAdminData } from "@providers/AdminDataProvider";
import { useVideoSessionData } from "@providers/VideoSessionProvider";

interface VideoCommentsProps {
  onClick: (time: number) => void;
  activeCommentId?: string | null;
  sessionRecorder: string;
  sessionNote?: string;
  /**
   * Optional pinned-note card rendered above the comment thread.
   * Used by inline mounts (e.g. expert chat overlay) to show the
   * originating chat message as the "A note from …" attachment card.
   */
  pinnedNote?: {
    authorFirstName: string;
    authorLastName: string;
    content: string;
    createdDate: string;
  };
}

type EmojiButton = {
  emoji: string;
  text: string;
  value: MediaCommentFilterEnum;
};

export const VideoComments: React.FC<VideoCommentsProps> = ({
  onClick,
  activeCommentId,
  sessionRecorder,
  sessionNote,
  pinnedNote,
}) => {
  const {
    commentsByMediaIdQuery: { isFetching },
    setFilteredCommentType,
    filteredCommentType,
    allFilteredComments,
  } = useVideoSessionData();

  const { roles, isUserRBT } = useAdminData();

  const [localActiveCommentId, setLocalActiveCommentId] = useState<string | null>(null);

  const effectiveActiveCommentId = activeCommentId || localActiveCommentId;

  const handleActiveCommentChange = (commentId: string | null) => {
    setLocalActiveCommentId(commentId);
  };

  const getFilterOptions = (userRole: "bcba" | "rbt" | "admin") => {
    const base: EmojiButton[] = [
      { emoji: "❤️", text: "Love", value: MediaCommentFilterEnum.Love },
      { emoji: "👍", text: "Praise", value: MediaCommentFilterEnum.Praise },
      { emoji: "💫", text: "Feedback", value: MediaCommentFilterEnum.Feedback },
      { emoji: "🤔", text: "Your Question", value: MediaCommentFilterEnum.YourQuestions },
    ];

    if (userRole === "bcba" || userRole === "admin") {
      base.push({ emoji: "🤔", text: "RBT’s Questions", value: MediaCommentFilterEnum.RbtQuestions });
    }
    if (userRole === "rbt" || userRole === "admin") {
      base.push({ emoji: "🤔", text: "BCBA’s Questions", value: MediaCommentFilterEnum.BcbaQuestions });
    }

    return base;
  };

  const filterOptions = getFilterOptions(isUserRBT ? "rbt" : roles.includes("admin") ? "admin" : "bcba").map(
    (item) => ({
      id: item.value,
      label: `${item.emoji} ${item.text}`,
    }),
  );

  return (
    <div className="w-full p-4 max-[640px]:p-3 flex flex-col gap-4 bg-white max-lg:min-h-[calc(100vh-18rem)]">
      <div className="flex flex-col gap-4">
        {filteredCommentType.length || allFilteredComments.length ? (
          <div className="relative shadow-[0_4px_8px_0px_rgba(0, 0, 0, 0.10)]">
            <SearchableDropdown
              items={filterOptions}
              label="Comment Type"
              placeholder=""
              onSelectionChange={(val) => setFilteredCommentType(val)}
              showSearch={false}
              selectedItems={filteredCommentType}
            />
          </div>
        ) : null}
        {pinnedNote ? (
          <SessionNoteCard
            author={`${pinnedNote.authorFirstName} ${pinnedNote.authorLastName}`.trim()}
            text={pinnedNote.content}
          />
        ) : null}
        {!sessionNote ? null : <SessionNoteCard author={sessionRecorder} text={sessionNote} />}
        {isFetching ? (
          <DataLoading text="Activity Loading" />
        ) : allFilteredComments.length ? (
          <div className="lg:overflow-y-auto lg:h-[calc(100vh-18rem)] flex flex-col">
            <CommentThread
              onClick={onClick}
              activeCommentId={effectiveActiveCommentId}
              onActiveCommentChange={handleActiveCommentChange}
            />
          </div>
        ) : filteredCommentType.length ? (
          <NoData
            title="No Comments Found"
            description="No comments matched your filters. You can try broadening your filter choices to see activity."
            showBorder={false}
          />
        ) : (
          <NoData
            title="No Activity Yet!"
            description="It looks like no feedback or comments have been added to the video yet. Start the conversation by leaving your first comment, or check back later for updates from others."
          />
        )}
      </div>
    </div>
  );
};
